import React, { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { MultiSelect } from '@/shared/components/ui/MultiSelect';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import {
  useLeadRoutingRules,
  useCreateLeadRoutingRule,
  useUpdateLeadRoutingRule,
  useDeleteLeadRoutingRule,
  useToggleLeadRoutingRule,
} from '../hooks/useLeadRoutingRules';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import {
  useLeadsCampaignsDropdown,
  useLeadsAdsetsDropdown,
  useLeadsAdsDropdown,
} from '@/modules/crm/leads/hooks/useLeadsDropdowns';
import type { LeadRoutingRuleResponse, LeadRoutingCondition } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
interface RoutingRulesTabProps {
  funnelId: string;
}

type ConditionField = LeadRoutingCondition['field'];
type ConditionOperator = LeadRoutingCondition['operator'];

const FIELD_OPTIONS: { value: ConditionField; label: string }[] = [
  { value: 'campaignName', label: 'Campanha' },
  { value: 'channel', label: 'Canal' },
  { value: 'origin', label: 'Origem' },
  { value: 'source', label: 'Fonte' },
  { value: 'medium', label: 'Meio' },
  { value: 'segment', label: 'Segmento' },
  { value: 'adName', label: 'Anúncio' },
  { value: 'adsetName', label: 'Conjunto de Anúncios' },
];

const OPERATOR_OPTIONS: { value: ConditionOperator; label: string }[] = [
  { value: 'equals', label: 'Igual a' },
  { value: 'contains', label: 'Contém' },
  { value: 'in', label: 'Um de (lista)' },
];

const CHANNEL_OPTIONS = [
  'whatsapp', 'instagram', 'facebook', 'messenger', 'telegram', 'email', 'website', 'phone',
  'google', 'youtube', 'tiktok', 'linkedin', 'bing', 'other',
];

const SOURCE_OPTIONS = [
  'meta', 'google', 'tiktok', 'linkedin', 'microsoft', 'organic', 'referral', 'email',
  'offline', 'partner', 'outbound', 'direct',
];

const MEDIUM_OPTIONS = [
  'organic', 'paid',
];

const getFieldLabel = (field: ConditionField): string => {
  return FIELD_OPTIONS.find((f) => f.value === field)?.label || field;
};

const getOperatorLabel = (operator: ConditionOperator): string => {
  return OPERATOR_OPTIONS.find((o) => o.value === operator)?.label || operator;
};

const formatConditionValue = (condition: LeadRoutingCondition): string => {
  if (Array.isArray(condition.value)) {
    return condition.value.join(', ');
  }
  return String(condition.value);
};

interface RuleFormState {
  name: string;
  description: string;
  assigneeIds: string[];
  priority: number;
  conditions: LeadRoutingCondition[];
}

const EMPTY_FORM: RuleFormState = {
  name: '',
  description: '',
  assigneeIds: [],
  priority: 1,
  conditions: [{ field: 'campaignName', operator: 'equals', value: '' }],
};

export const RoutingRulesTab: React.FC<RoutingRulesTabProps> = ({ funnelId }) => {
  const confirm = useConfirm();
  const { data: rules, isLoading } = useLeadRoutingRules(funnelId);
  const { data: users = [] } = useUsersDropdown({ status: 'active' });
  const createRule = useCreateLeadRoutingRule();
  const updateRule = useUpdateLeadRoutingRule();
  const deleteRule = useDeleteLeadRoutingRule();
  const toggleRule = useToggleLeadRoutingRule();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<LeadRoutingRuleResponse | null>(null);
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);

  const handleOpenCreate = () => {
    setEditingRule(null);
    setForm(EMPTY_FORM);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (rule: LeadRoutingRuleResponse) => {
    setEditingRule(rule);
    setForm({
      name: rule.name,
      description: rule.description || '',
      assigneeIds: rule.assigneeIds ?? [],
      priority: rule.priority,
      conditions: rule.conditions.length > 0
        ? rule.conditions
        : [{ field: 'campaignName', operator: 'equals', value: '' }],
    });
    setIsFormOpen(true);
  };

  const handleCancel = () => {
    setIsFormOpen(false);
    setEditingRule(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.name.trim() || form.assigneeIds.length === 0) return;

    const validConditions = form.conditions.filter((c) => {
      if (Array.isArray(c.value)) return c.value.length > 0;
      return String(c.value).trim() !== '';
    });

    if (validConditions.length === 0) return;

    try {
      if (editingRule) {
        await updateRule.mutateAsync({
          id: editingRule.id,
          funnelId,
          data: {
            name: form.name,
            description: form.description || undefined,
            assigneeIds: form.assigneeIds,
            priority: form.priority,
            conditions: validConditions,
          },
        });
      } else {
        await createRule.mutateAsync({
          name: form.name,
          description: form.description || undefined,
          funnelId,
          assigneeIds: form.assigneeIds,
          priority: form.priority,
          conditions: validConditions,
        });
      }
      handleCancel();
    } catch {
      // toast already handled in hooks
    }
  };

  const handleDelete = async (rule: LeadRoutingRuleResponse) => {
    // NUNCA window.confirm — modal de confirmação via ConfirmProvider (promise-based)
    const confirmed = await confirm({
      title: 'Excluir regra',
      message: `Deseja excluir a regra "${rule.name}"?`,
      confirmButtonText: 'Excluir',
      variant: 'danger',
    });
    if (!confirmed) return;
    await deleteRule.mutateAsync({ id: rule.id, funnelId });
  };

  const handleToggle = async (rule: LeadRoutingRuleResponse) => {
    const isActive = rule.status === 'active';
    await toggleRule.mutateAsync({ id: rule.id, active: !isActive, funnelId });
  };

  // Condition management
  const addCondition = () => {
    setForm((prev) => ({
      ...prev,
      conditions: [...prev.conditions, { field: 'campaignName', operator: 'equals', value: '' }],
    }));
  };

  const removeCondition = (index: number) => {
    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const updateCondition = (index: number, updates: Partial<LeadRoutingCondition>) => {
    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => {
        if (i !== index) return c;
        const updated = { ...c, ...updates };
        // Reset value when changing operator to/from 'in'
        if (updates.operator) {
          if (updates.operator === 'in' && !Array.isArray(c.value)) {
            updated.value = c.value ? [String(c.value)] : [];
          } else if (updates.operator !== 'in' && Array.isArray(c.value)) {
            updated.value = c.value[0] || '';
          }
        }
        return updated;
      }),
    }));
  };

  const getUserName = (userId: string): string => {
    const user = users.find((u: { id: string; name: string }) => u.id === userId);
    return user ? user.name : userId;
  };

  const sortedRules = [...(rules || [])].sort((a, b) => a.priority - b.priority);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3
            className="text-sm font-semibold"
            style={{ color: '#1e293b' }}
          >
            Regras de Roteamento
          </h3>
          <p
            className="text-xs mt-0.5"
            style={{ color: '#64748b' }}
          >
            Atribua leads automaticamente com base em condições
          </p>
        </div>
        {!isFormOpen && (
          <Button
            onClick={handleOpenCreate}
            variant="primary"
            icon="plus"
            data-testid="routing-rules-new"
          >
            Nova Regra
          </Button>
        )}
      </div>

      {/* Form */}
      {isFormOpen && (
        <div
          className="border rounded-lg p-4 space-y-4"
          style={{ borderColor: '#d1d5db', backgroundColor: '#f9fafb' }}
        >
          <h4 className="text-sm font-medium" style={{ color: '#1e293b' }}>
            {editingRule ? 'Editar Regra' : 'Nova Regra'}
          </h4>

          {/* Name */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: '#1e293b' }}>
              Nome da Regra *
            </label>
            <Input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex: Leads do Google Ads"
              data-testid="routing-rules-name"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: '#1e293b' }}>
              Descrição
            </label>
            <Input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Descrição opcional"
              data-testid="routing-rules-description"
            />
          </div>

          {/* Assignee(s) */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: '#1e293b' }}>
              Atribuir para *
            </label>
            <MultiSelect
              options={users.map((user: { id: string; name: string; email: string }) => ({
                value: user.id,
                label: `${user.name} (${user.email})`,
              }))}
              selectedValues={form.assigneeIds}
              onChange={(values) => setForm({ ...form, assigneeIds: values })}
              placeholder="Selecione usuários..."
            />
            {form.assigneeIds.length > 1 && (
              <p className="text-xs mt-1" style={{ color: '#8b5cf6' }}>
                Round-robin entre {form.assigneeIds.length} usuários
              </p>
            )}
          </div>

          {/* Priority */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: '#1e293b' }}>
              Prioridade (menor = mais prioritário)
            </label>
            <div className="w-24">
              <Input
                type="number"
                min={1}
                max={100}
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 1 })}
                data-testid="routing-rules-priority"
              />
            </div>
          </div>

          {/* Conditions */}
          <div>
            <label className="block text-xs font-medium mb-2" style={{ color: '#1e293b' }}>
              Condições (todas devem ser atendidas)
            </label>
            <div className="space-y-2">
              {form.conditions.map((condition, index) => (
                <ConditionRow
                  key={index}
                  condition={condition}
                  onChange={(updates) => updateCondition(index, updates)}
                  onRemove={() => removeCondition(index)}
                  canRemove={form.conditions.length > 1}
                />
              ))}
            </div>
            <button
              onClick={addCondition}
              className="mt-2 flex items-center gap-1 text-xs font-medium transition-colors"
              style={{ color: '#8b5cf6' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#7c3aed'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#8b5cf6'; }}
            >
              <Icon name="plus" size={12} />
              Adicionar condição
            </button>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button onClick={handleCancel} variant="secondary" className="text-sm">
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              variant="primary"
              className="text-sm"
              disabled={
                !form.name.trim() ||
                form.assigneeIds.length === 0 ||
                createRule.isPending ||
                updateRule.isPending
              }
            >
              {editingRule ? 'Salvar' : 'Criar Regra'}
            </Button>
          </div>
        </div>
      )}

      {/* Rules List */}
      {isLoading ? (
        <div className="text-center py-6 text-sm" style={{ color: '#64748b' }}>
          Carregando regras...
        </div>
      ) : sortedRules.length === 0 && !isFormOpen ? (
        <div className="text-center py-6 text-sm" style={{ color: '#64748b' }}>
          Nenhuma regra de roteamento cadastrada.
        </div>
      ) : (
        <div className="space-y-2">
          {sortedRules.map((rule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              getUserName={getUserName}
              onEdit={() => handleOpenEdit(rule)}
              onDelete={() => handleDelete(rule)}
              onToggle={() => handleToggle(rule)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

RoutingRulesTab.displayName = 'RoutingRulesTab';

// ============================================================================
// ConditionRow Component
// ============================================================================

interface ConditionRowProps {
  condition: LeadRoutingCondition;
  onChange: (updates: Partial<LeadRoutingCondition>) => void;
  onRemove: () => void;
  canRemove: boolean;
}

const DYNAMIC_DROPDOWN_FIELDS: ConditionField[] = ['campaignName', 'adName', 'adsetName'];
const STATIC_SELECT_FIELDS: ConditionField[] = ['channel', 'source', 'medium'];

const ConditionRow: React.FC<ConditionRowProps> = ({ condition, onChange, onRemove, canRemove }) => {
  const { data: campaigns = [] } = useLeadsCampaignsDropdown();
  const { data: adsets = [] } = useLeadsAdsetsDropdown();
  const { data: ads = [] } = useLeadsAdsDropdown();

  const getDynamicOptions = (field: ConditionField): string[] => {
    if (field === 'campaignName') return campaigns;
    if (field === 'adsetName') return adsets;
    if (field === 'adName') return ads;
    return [];
  };

  const isDynamicField = DYNAMIC_DROPDOWN_FIELDS.includes(condition.field);
  const isStaticSelectField = STATIC_SELECT_FIELDS.includes(condition.field);
  const staticOptions = condition.field === 'channel'
    ? CHANNEL_OPTIONS
    : condition.field === 'medium'
      ? MEDIUM_OPTIONS
      : SOURCE_OPTIONS;
  const dynamicOptions = isDynamicField ? getDynamicOptions(condition.field) : [];

  const renderValueInput = (): React.ReactNode => {
    // Dynamic dropdown fields (campaign, ad, adset)
    if (isDynamicField) {
      if (condition.operator === 'in') {
        // Multi-select for "in" operator
        return (
          <MultiSelect
            options={dynamicOptions.map((opt) => ({ value: opt, label: opt }))}
            selectedValues={Array.isArray(condition.value) ? condition.value : (condition.value ? [String(condition.value)] : [])}
            onChange={(values) => onChange({ value: values })}
            placeholder="Selecione..."
          />
        );
      }
      if (condition.operator === 'equals') {
        // Single select for "equals" operator
        return (
          <Select
            value={String(condition.value)}
            onChange={(value) => onChange({ value })}
            options={dynamicOptions.map((opt) => ({ value: opt, label: opt }))}
            placeholder="Selecione..."
            data-testid="routing-rules-condition-value"
          />
        );
      }
      // "contains" operator - text input for partial matching
      return (
        <Input
          type="text"
          value={String(condition.value)}
          onChange={(e) => onChange({ value: e.target.value })}
          placeholder="Valor"
          data-testid="routing-rules-condition-value"
        />
      );
    }

    // Static select fields (origin, source)
    if (isStaticSelectField && condition.operator !== 'in') {
      return (
        <Select
          value={String(condition.value)}
          onChange={(value) => onChange({ value })}
          options={staticOptions.map((opt) => ({ value: opt, label: opt }))}
          placeholder="Selecione..."
          data-testid="routing-rules-condition-value"
        />
      );
    }

    // "in" operator - comma-separated text input
    if (condition.operator === 'in') {
      return (
        <Input
          type="text"
          value={Array.isArray(condition.value) ? condition.value.join(', ') : String(condition.value)}
          onChange={(e) => onChange({ value: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) })}
          placeholder="valor1, valor2, valor3"
          data-testid="routing-rules-condition-value"
        />
      );
    }

    // Default text input
    return (
      <Input
        type="text"
        value={String(condition.value)}
        onChange={(e) => onChange({ value: e.target.value })}
        placeholder="Valor"
        data-testid="routing-rules-condition-value"
      />
    );
  };

  return (
    <div className="flex items-start gap-2">
      {/* Field */}
      <div className="w-[160px] flex-shrink-0">
        <Select
          value={condition.field}
          onChange={(value) => onChange({ field: value as ConditionField, value: '' })}
          options={FIELD_OPTIONS.map((f) => ({ value: f.value, label: f.label }))}
          data-testid="routing-rules-condition-field"
        />
      </div>

      {/* Operator */}
      <div className="w-[110px] flex-shrink-0">
        <Select
          value={condition.operator}
          onChange={(value) => onChange({ operator: value as ConditionOperator })}
          options={OPERATOR_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          data-testid="routing-rules-condition-operator"
        />
      </div>

      {/* Value */}
      <div className="flex-1">
        {renderValueInput()}
      </div>

      {/* Remove */}
      {canRemove && (
        <button
          onClick={onRemove}
          className="p-1.5 rounded transition-colors flex-shrink-0"
          style={{ color: '#ef4444' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <Icon name="trash" size={14} />
        </button>
      )}
    </div>
  );
};

// ============================================================================
// RuleCard Component
// ============================================================================

interface RuleCardProps {
  rule: LeadRoutingRuleResponse;
  getUserName: (userId: string) => string;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
}

const RuleCard: React.FC<RuleCardProps> = ({ rule, getUserName, onEdit, onDelete, onToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const isActive = rule.status === 'active';

  return (
    <div
      className="border rounded-lg overflow-hidden"
      style={{
        borderColor: isActive ? '#e5e7eb' : '#f3f4f6',
        opacity: isActive ? 1 : 0.7,
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2"
        style={{ backgroundColor: '#f9fafb' }}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-0.5 rounded"
            style={{ color: '#64748b' }}
          >
            {expanded ? <Icon name="chevron-up" size={14} /> : <Icon name="chevron-down" size={14} />}
          </button>

          <span
            className="text-xs font-medium px-1.5 py-0.5 rounded"
            style={{ backgroundColor: '#e5e7eb', color: '#64748b' }}
          >
            #{rule.priority}
          </span>

          <span
            className="text-sm font-medium truncate"
            style={{ color: '#1e293b' }}
          >
            {rule.name}
          </span>

          <span
            className="text-xs px-1.5 py-0.5 rounded-full"
            style={{
              backgroundColor: isActive ? '#dcfce7' : '#f3f4f6',
              color: isActive ? '#16a34a' : '#9ca3af',
            }}
          >
            {isActive ? 'Ativa' : 'Inativa'}
          </span>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Toggle */}
          <button
            onClick={onToggle}
            className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
            style={{ backgroundColor: isActive ? '#8b5cf6' : '#d1d5db' }}
          >
            <span
              className="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform"
              style={{ transform: isActive ? 'translateX(18px)' : 'translateX(3px)' }}
            />
          </button>

          <button
            onClick={onEdit}
            className="p-1 rounded transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <Icon name="edit" size={14} />
          </button>

          <button
            onClick={onDelete}
            className="p-1 rounded transition-colors"
            style={{ color: '#ef4444' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <Icon name="trash" size={14} />
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-3 py-2 space-y-2" style={{ borderTop: '1px solid #e5e7eb' }}>
          {rule.description && (
            <p className="text-xs" style={{ color: '#64748b' }}>
              {rule.description}
            </p>
          )}

          <div className="text-xs" style={{ color: '#1e293b' }}>
            <span style={{ color: '#64748b' }}>Atribuir para: </span>
            {(() => {
              const ids = rule.assigneeIds ?? [];
              return (
                <>
                  <span className="font-medium">
                    {ids.map((id) => getUserName(id)).join(', ')}
                  </span>
                  {ids.length > 1 && (
                    <span
                      className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                      style={{ backgroundColor: '#ede9fe', color: '#7c3aed' }}
                    >
                      round-robin
                    </span>
                  )}
                </>
              );
            })()}
          </div>

          <div>
            <span className="text-xs" style={{ color: '#64748b' }}>Condições:</span>
            <div className="mt-1 space-y-1">
              {rule.conditions.map((condition, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded"
                  style={{ backgroundColor: '#f1f5f9' }}
                >
                  <span className="font-medium" style={{ color: '#1e293b' }}>
                    {getFieldLabel(condition.field)}
                  </span>
                  <span style={{ color: '#8b5cf6' }}>
                    {getOperatorLabel(condition.operator)}
                  </span>
                  <span style={{ color: '#1e293b' }}>
                    &ldquo;{formatConditionValue(condition)}&rdquo;
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
